import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SecurityContext,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { Subscription, firstValueFrom } from 'rxjs';
import { MessageSegmentKind, renderMarkdownToHtml, splitMessageSegments } from '../../markdown.util';
import { htmlToPlainText } from '../../plain-text.util';
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_BYTES,
  ATTACHMENT_MAX_COUNT,
  AttachmentUploadTicket,
  ChatMessage,
  ChatSession,
  FeedbackRating,
  MessageAttachment,
  ModelOption,
  ThinkingLevelOption,
  PebblComponent,
  PendingAttachment,
  TableComponent,
  UsageInfo,
  formatBytes,
  isAllowedAttachment,
} from '../../ai.models';
import { PebblApiService } from '../../pebbl-api.service';
import { ChatStreamStore } from '../../chat-stream.store';
import { AuthService } from '@coreservices';
import {
  ChatFeedbackPopupComponent,
  FeedbackDetail,
} from '../chat-feedback-popup/chat-feedback-popup.component';
import { encodeFeedbackMessage } from '../../feedback-context.util';
import { uuidV4 } from '../../uuid.util';
import { stripInlineHandlers } from '../../../admin-core/utils/strip-inline-handlers';
import {
  ReplyEntrySegment,
  cardedProducts,
  markRecommendedEntry,
  pairEntries,
  placeProductCards,
} from '../../product-cards.util';
import { ProductCardsComponent } from '../product-cards/product-cards.component';
import { ProductEntryComponent } from '../product-entry/product-entry.component';

/** A rendered message segment for the template: prose, a product entry, or an email-draft card. */
interface RenderedSegment {
  /** Stable per-message id ('m0', 'e1'…) — the "Copied" state key for email drafts. */
  key: string;
  kind: MessageSegmentKind;
  /** HTML already run through Angular's HTML sanitizer — bound to `[innerHTML]`
   *  as a plain string (never `SafeHtml`), so Angular sanitizes it again there. */
  html: string;
  /** The same sanitized HTML, for the "Copy email" clipboard payload, which is
   *  written with `element.innerHTML` and so bypasses the template sanitizer. */
  rawHtml: string;
  /** This entry's number in the reply; null for plain text. */
  entry: number | null;
  /** Index into `MessageParts.skus` of the card whose `product_link` this entry links to;
   *  null where none matches (or the reply has no SKUs and the picture is looked up by link). */
  card: number | null;
  /** An entry directly under another entry — draws the divider between the two. */
  followsEntry: boolean;
  /** A link-matched entry's storefront product link and name (no SKUs on the reply). */
  href?: string;
  name?: string;
}

/** An assistant answer cut into prose, product entries and email drafts (see messageParts). */
interface MessageParts {
  segments: RenderedSegment[];
  /** Every `product_cards` SKU, in order; empty means no pictures. */
  skus: string[];
  /** How many entries the reply has. */
  placed: number;
  /** SKUs no entry took, in order — the strip under the settled reply. */
  stripSkus: string[];
}

/** Fallback chat model, used until `GET /pebbl/models` names the backend's default. */
const DEFAULT_MODEL = 'anthropic/claude-haiku-5.5';
/** Fallback thinking level, used until the roster names the model's own default. */
const DEFAULT_THINKING_LEVEL = 'medium';
/** First-paint thinking levels, replaced by the selected model's own list from the roster. */
const FALLBACK_THINKING_LEVELS: ThinkingLevelOption[] = [
  { value: 'low', label: 'Low', description: 'fastest replies, least reasoning' },
  { value: 'medium', label: 'Medium', description: 'balanced speed and depth' },
  { value: 'high', label: 'High', description: 'more thorough reasoning' },
];
/** Levels the backend may list for a model but the picker never offers: they can reason
 *  for minutes before the first answer text arrives. */
const HIDDEN_THINKING_LEVELS = new Set(['xhigh', 'max']);
/** Cookie holding the last model the user chose, so it's restored on the next visit. */
const MODEL_COOKIE = 'pebbl_model';
/** Cookie holding the last thinking level the user chose. */
const THINKING_LEVEL_COOKIE = 'pebbl_thinking_level';
/** How long the remembered choices persist (1 year). */
const MODEL_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

/**
 * The chat pane — a thin projection of the active conversation's {@link ChatSession},
 * which lives in {@link ChatStreamStore}. The store owns every thread's transcript and
 * live SSE stream, so streams keep running (and are rejoined intact) when the user
 * switches away, and several conversations can stream in parallel. This component only
 * renders the active session, drives the composer, and handles view-local concerns
 * (auto-scroll, clipboard, markdown, the model picker). Turn boundaries and new-chat
 * creation are reported up via the store's subjects, which the shell subscribes to.
 */
@Component({
  selector: 'app-ai-chat-view',
  standalone: true,
  imports: [CommonModule, FormsModule, ChatFeedbackPopupComponent, ProductCardsComponent, ProductEntryComponent],
  templateUrl: './chat-view.component.html',
  styleUrls: ['./chat-view.component.css'],
})
export class ChatViewComponent implements OnInit, OnChanges, OnDestroy {
  /* ################### Variable Declaration Start ################### */
  @Input() activeId: string | null = null;

  /** When true (the floating widget, which has no shell to manage `activeId`), the chat
   *  view adopts a just-created conversation itself so streaming updates render live. */
  @Input() selfManageSession = false;

  /** Logged-in user's first name for the welcome greeting; falls back to "there". */
  get greetingName(): string {
    const name = (this.authService.currentUserValue?.first_name ?? '').toString().trim();
    return name || 'there';
  }

  /** Format a message's ISO timestamp as e.g. "2 Aug, 2:44 PM" (empty when absent —
   *  live turns are stamped client-side; server-loaded history has no per-message time). */
  formatMsgTime(iso?: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const date = d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
    const time = d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${date}, ${time.toUpperCase()}`;
  }

  /** Scroll container + bottom sentinel for auto-scroll while streaming. */
  @ViewChild('log') logRef?: ElementRef<HTMLDivElement>;
  @ViewChild('endAnchor') endRef?: ElementRef<HTMLDivElement>;
  /** The composer textarea, for auto-growing and resetting after a send. */
  @ViewChild('composerInput') taRef?: ElementRef<HTMLTextAreaElement>;

  /** Whether the log is pinned near its bottom — drives auto-scroll + the jump button. */
  atBottom = true;
  /** Skips the next auto-scroll when older messages are prepended. */
  private suppressScroll = false;

  /** The active conversation's session, or null on the new-chat welcome screen. */
  private session: ChatSession | null = null;
  /** Composer draft for the not-yet-created new chat (no session to hold it yet). */
  pendingNewInput = '';
  /** Inline error pair shown when a brand-new chat fails to initialise (no session). */
  private failedNewChat: ChatMessage[] = [];

  /**
   * Selectable chat models. Seeded with the default so the picker is visible from
   * the first paint; replaced by the real roster once `GET /pebbl/models` returns.
   */
  models: ModelOption[] = [
    {
      value: DEFAULT_MODEL,
      label: 'Claude Haiku 5.5',
      description: 'fast everyday questions',
      thinkingLevels: FALLBACK_THINKING_LEVELS,
      defaultThinkingLevel: DEFAULT_THINKING_LEVEL,
    },
  ];
  /** The active model id, restored from the cookie and sent with each chat turn. */
  selectedModel: string = DEFAULT_MODEL;
  /** The active thinking level, restored from the cookie and sent with each chat turn. */
  selectedThinkingLevel: string = DEFAULT_THINKING_LEVEL;
  /** Whether the model menu is open. */
  modelOpen = false;
  /** Whether the thinking-level menu is open. */
  thinkingOpen = false;
  /** The picker wrappers, so an outside click can close their menus. */
  @ViewChild('modelSelect') modelSelectRef?: ElementRef<HTMLDivElement>;
  @ViewChild('thinkingSelect') thinkingSelectRef?: ElementRef<HTMLDivElement>;

  copiedAnswer: number | null = null;
  copiedTable: number | null = null;
  /** Key (`messageIndex:segmentIndex`) of the email draft showing "Copied". */
  copiedEmail: string | null = null;

  /** The assistant message awaiting thumbs-down detail, or null when the popup is closed. */
  feedbackTarget: ChatMessage | null = null;

  /* ---- Image attachments ---- */

  /** Limits + accept list surfaced to the template. */
  readonly maxAttachments = ATTACHMENT_MAX_COUNT;
  readonly attachmentAccept = ATTACHMENT_ACCEPT;

  /** Images picked for the next turn, with their per-file upload state. */
  attachments: PendingAttachment[] = [];
  /** Why the last pick was (partly) rejected — shown under the strip. */
  attachError: string | null = null;
  /** The sent image blown up in the full-size overlay, or null when closed. */
  lightbox: MessageAttachment | null = null;
  /** True while a file is dragged over the composer box. */
  dragOver = false;

  /**
   * The conversation the current attachments (pending and just-sent previews) are scoped
   * to. Images are authorised against one conversation server-side, so a brand-new chat
   * commits to a client-minted id the moment the first file is picked; the chat endpoint
   * then creates the conversation under that id. Abandoning the draft leaves no empty
   * conversation behind — nothing is created until the turn actually runs.
   */
  private attachmentScopeId: string | null = null;
  /** Object URLs handed to sent bubbles; released when the conversation changes. */
  private sentPreviewUrls: string[] = [];
  /** Sent images whose URL has already been re-resolved once (expiry recovery). */
  private refreshedImages = new Set<string>();
  /** Sent images that failed even after a re-resolve — rendered as "unavailable". */
  private brokenImages = new Set<string>();

  private destroyed = false;
  private sub = new Subscription();
  /* ################### Variable Declaration End ################### */

  /* ################### Constructor Declaration Start ################### */
  constructor(
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private api: PebblApiService,
    private store: ChatStreamStore,
    private authService: AuthService,
  ) {}
  /* ################### Constructor Declaration End ################### */

  /************************************
   * Session projection (getters) start
   ************************************/
  get messages(): ChatMessage[] {
    return this.session?.messages ?? this.failedNewChat;
  }
  get status(): string | null {
    return this.session?.status ?? null;
  }
  get loadingMessages(): boolean {
    return this.session?.loadingMessages ?? false;
  }
  get msgCursor(): number | null {
    return this.session?.msgCursor ?? null;
  }
  /** True when the active conversation has a turn in flight (per-thread). */
  get streaming(): boolean {
    return this.session?.streaming ?? false;
  }
  /** The view only ever projects the active session, so "viewing a stream" == streaming. */
  get viewingStream(): boolean {
    return this.streaming;
  }
  /** Two-way composer draft: session-backed, or the pending buffer for a new chat. */
  get input(): string {
    return this.session ? this.session.input : this.pendingNewInput;
  }
  set input(value: string) {
    if (this.session) this.store.setDraft(this.session.id, value);
    else this.pendingNewInput = value;
  }
  /************************************
   * Session projection (getters) end
   ************************************/

  /************************************
   * Life cycle hooks start
   ************************************/
  ngOnInit(): void {
    // Restore the remembered choice up front so the picker shows it without a flash;
    // loadModels() then reconciles it against the models the backend actually offers.
    this.selectedModel = this.readCookie(MODEL_COOKIE) || DEFAULT_MODEL;
    // Resolved against the fallback levels, so a remembered hidden level ("max") never
    // goes out with a turn even if the roster request fails.
    this.selectedThinkingLevel = this.resolveThinkingLevel(this.readCookie(THINKING_LEVEL_COOKIE));
    this.loadModels();
    // Repaint the active view whenever its session changes (stream tokens or REST loads).
    // Background-conversation changes carry a non-active id and are ignored here, so an
    // off-screen stream never thrashes the visible view.
    this.sub.add(
      this.store.changes$.subscribe((id) => {
        if (id === this.activeId) this.detectChanges();
      }),
    );
    // Widget (no shell to set activeId): when a brand-new chat mints its conversation,
    // adopt it immediately so streaming repaints — the "Thinking…" state and each streamed
    // token — target this session, instead of nothing showing until runQuery resolves.
    this.sub.add(
      this.store.sessionCreated$.subscribe((conv) => {
        if (this.selfManageSession && !this.activeId) {
          this.activeId = conv.id;
          this.session = this.store.getSession(conv.id) ?? this.session;
          this.detectChanges();
        }
      }),
    );
    if (this.activeId) this.bindActiveSession();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeId'] && !changes['activeId'].firstChange) {
      this.bindActiveSession();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.sub.unsubscribe();
    this.releasePreviewUrls();
    // Deliberately NOT aborting: streams live in the store and must survive this view's
    // teardown (e.g. switching to the knowledge/search pane). Only stop() / delete abort.
  }

  /**
   * Point the view at the active conversation's session. A session this component just
   * created mid-turn (selfCreated) is left as-is — reloading would clobber the live turn;
   * otherwise we ask the store to load history, which no-ops if the session is already
   * streaming or loaded (that's the rejoin path).
   */
  private bindActiveSession(): void {
    this.atBottom = true;
    this.suppressScroll = false;
    this.failedNewChat = [];
    // Attachments belong to one chat's composer. Skip the discard when the conversation
    // we just bound to is the very one they were scoped to — that's the new-chat handoff
    // right after a send, where the sent bubble is still showing its local previews.
    if (this.activeId !== this.attachmentScopeId) this.discardAttachments();
    if (!this.activeId) {
      this.session = null;
      this.detectChanges();
      return;
    }
    const s = this.store.getOrCreateSession(this.activeId);
    this.session = s;
    if (s.selfCreated) {
      s.selfCreated = false;
    } else if (!s.streaming) {
      this.store.loadMessages(this.activeId);
    }
    this.detectChanges();
  }

  /** Change detection guarded against a destroyed view (async callbacks may outlive it). */
  private detectChanges(): void {
    if (this.destroyed) return;
    this.cdr.detectChanges();
    this.maybeAutoScroll();
  }
  /************************************
   * Life cycle hooks end
   ************************************/

  /************************************
   * Model selection start
   ************************************/
  /**
   * Load the available models and settle on the active model and thinking level. A
   * remembered choice is kept only while the roster still offers it (this is what clears
   * retired ids such as `openai/gpt-5.4-mini`); otherwise the backend's default wins.
   */
  private loadModels(): void {
    this.api.listModels().subscribe({
      next: ({ models, defaultModel }) => {
        // Keep the seeded fallback if the backend returned no models. Hidden levels are
        // dropped here for every model, so the picker and resolveThinkingLevel never see them.
        if (models.length) {
          this.models = models.map((m) => ({
            ...m,
            thinkingLevels: m.thinkingLevels.filter((l) => !HIDDEN_THINKING_LEVELS.has(l.value)),
          }));
        }
        const has = (id: string | null): boolean => !!id && this.models.some((m) => m.value === id);
        const remembered = this.readCookie(MODEL_COOKIE);
        this.selectedModel =
          (has(remembered) && remembered) ||
          (has(defaultModel) && defaultModel!) ||
          (has(DEFAULT_MODEL) && DEFAULT_MODEL) ||
          this.models[0]?.value ||
          DEFAULT_MODEL;
        this.selectedThinkingLevel = this.resolveThinkingLevel(this.readCookie(THINKING_LEVEL_COOKIE));
        this.detectChanges();
      },
      error: () => {
        // Keep whatever the cookie/default gave us; the picker just stays hidden.
        this.detectChanges();
      },
    });
  }

  /** The selected model's roster entry, if the roster lists it. */
  private get selectedModelOption(): ModelOption | undefined {
    return this.models.find((m) => m.value === this.selectedModel);
  }

  /** Name shown on the closed picker; falls back to the raw id for an unknown model. */
  get selectedModelLabel(): string {
    return this.selectedModelOption?.label || this.selectedModel;
  }

  /** The levels the selected model accepts, lowest effort first. */
  get thinkingLevels(): ThinkingLevelOption[] {
    const levels = this.selectedModelOption?.thinkingLevels;
    return levels?.length ? levels : FALLBACK_THINKING_LEVELS;
  }

  /** Name shown on the closed thinking-level picker. */
  get selectedThinkingLabel(): string {
    return this.thinkingLevels.find((l) => l.value === this.selectedThinkingLevel)?.label || this.selectedThinkingLevel;
  }

  /** `wanted` if the selected model accepts it, else that model's own default level. */
  private resolveThinkingLevel(wanted: string | null): string {
    const levels = this.thinkingLevels;
    const has = (id: string | null): boolean => !!id && levels.some((l) => l.value === id);
    const modelDefault = this.selectedModelOption?.defaultThinkingLevel ?? null;
    return (
      (has(wanted) && wanted!) ||
      (has(modelDefault) && modelDefault!) ||
      (has(DEFAULT_THINKING_LEVEL) && DEFAULT_THINKING_LEVEL) ||
      levels[0]?.value ||
      DEFAULT_THINKING_LEVEL
    );
  }

  toggleModelOpen(): void {
    this.modelOpen = !this.modelOpen;
    this.thinkingOpen = false;
  }

  toggleThinkingOpen(): void {
    this.thinkingOpen = !this.thinkingOpen;
    this.modelOpen = false;
  }

  /** Pick a model from the menu and persist it so it's auto-selected next visit. */
  selectModel(model: ModelOption): void {
    this.modelOpen = false;
    this.onModelChange(model.value);
  }

  /** Pick a thinking level from the menu and persist it so it's auto-selected next visit. */
  selectThinkingLevel(level: ThinkingLevelOption): void {
    this.thinkingOpen = false;
    this.selectedThinkingLevel = level.value;
    this.writeCookie(THINKING_LEVEL_COOKIE, level.value, MODEL_COOKIE_MAX_AGE);
  }

  /** Close the menus on any click outside their picker (the composer, the log, elsewhere). */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.modelOpen && !this.thinkingOpen) return;
    const target = event.target as Node;
    let changed = false;
    if (this.modelOpen && !this.modelSelectRef?.nativeElement.contains(target)) {
      this.modelOpen = false;
      changed = true;
    }
    if (this.thinkingOpen && !this.thinkingSelectRef?.nativeElement.contains(target)) {
      this.thinkingOpen = false;
      changed = true;
    }
    if (changed) this.detectChanges();
  }

  /** Persist the newly chosen model. The thinking level carries over when the new model
   *  accepts it, and resets to that model's default when it doesn't. */
  onModelChange(model: string): void {
    if (!model) return;
    this.selectedModel = model;
    this.writeCookie(MODEL_COOKIE, model, MODEL_COOKIE_MAX_AGE);
    const level = this.resolveThinkingLevel(this.selectedThinkingLevel);
    if (level !== this.selectedThinkingLevel) {
      this.selectedThinkingLevel = level;
      this.writeCookie(THINKING_LEVEL_COOKIE, level, MODEL_COOKIE_MAX_AGE);
    }
  }
  /************************************
   * Model selection end
   ************************************/

  /************************************
   * Auto-scroll start
   ************************************/
  /** Track whether the log is pinned near its bottom (mirrors the ChatView.tsx logic). */
  onLogScroll(): void {
    const el = this.logRef?.nativeElement;
    if (!el) return;
    this.atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }

  /** Jump to the newest message (floating down-arrow button). */
  scrollToBottom(): void {
    this.atBottom = true;
    this.endRef?.nativeElement.scrollIntoView({ behavior: 'smooth' });
  }

  /**
   * Keep the newest content in view during a stream / after new messages, but only
   * when the user is already pinned to the bottom — and never right after older
   * messages were prepended (loadOlderMessages sets suppressScroll).
   */
  private maybeAutoScroll(): void {
    if (this.suppressScroll) {
      this.suppressScroll = false;
      return;
    }
    if (!this.atBottom) return;
    this.endRef?.nativeElement.scrollIntoView({ behavior: 'smooth' });
  }
  /************************************
   * Auto-scroll end
   ************************************/

  /************************************
   * Message loading start
   ************************************/
  loadOlderMessages(): void {
    if (!this.activeId || this.msgCursor == null) return;
    // Prepending older history must not yank the view to the bottom.
    this.suppressScroll = true;
    this.store.loadOlderMessages(this.activeId);
  }
  /************************************
   * Message loading end
   ************************************/

  /************************************
   * Composer start
   ************************************/
  onEnter(event: Event): void {
    const ke = event as KeyboardEvent;
    if (ke.shiftKey) return;
    event.preventDefault();
    void this.runQuery(this.input);
  }

  /** Grow the composer textarea with its content, capped at the CSS max-height. */
  autoGrow(el: HTMLTextAreaElement): void {
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 200) + 'px';
  }

  /** Collapse the composer back to a single row (after a send clears the input). */
  private resetComposerHeight(): void {
    const el = this.taRef?.nativeElement;
    if (el) el.style.height = 'auto';
  }

  async runQuery(text: string): Promise<void> {
    const trimmed = (text || '').trim();
    const ready = this.readyAttachments;
    // Per-thread lock: block only a second turn to THIS conversation (also disabled in
    // the template). Other conversations remain free to stream in parallel. An image-only
    // turn is allowed, but never one while a file is still uploading.
    if ((!trimmed && ready.length === 0) || this.streaming || this.uploadInFlight) return;

    this.failedNewChat = [];
    this.input = '';
    this.resetComposerHeight();

    // Carry the local previews onto the optimistic bubble so the images stay visible
    // while the turn streams; the reload on a later visit swaps in the presigned URLs.
    const sent: MessageAttachment[] = ready.map((item) => ({
      attachmentUuid: item.attachmentUuid as string,
      url: item.previewUrl,
      fileName: item.file.name,
      contentType: item.file.type,
      sizeBytes: item.file.size,
    }));
    // Hand the object URLs to the bubble rather than revoking them here; they are
    // released when the composer leaves this conversation.
    this.sentPreviewUrls.push(...sent.map((a) => a.url));
    this.attachments = [];
    this.attachError = null;

    // A new chat carrying images already committed to a conversation id (see
    // attachmentScopeId); reuse it so the ids the backend authorised still line up.
    const reservedId = !this.activeId && sent.length ? this.attachmentScopeId ?? undefined : undefined;

    const id = await this.store.runQuery(
      this.activeId,
      trimmed,
      this.selectedModel,
      this.selectedThinkingLevel,
      sent,
      reservedId,
    );
    if (id === null) {
      // A brand-new chat failed to initialise — no session exists, so show the error
      // pair in the session-less view.
      this.failedNewChat = [
        { role: 'user', text: trimmed, timeline: [], components: [], attachments: sent },
        {
          role: 'assistant',
          text: '',
          error: 'Couldn’t start a new chat. Please try again.',
          timeline: [],
          components: [],
        },
      ];
      this.detectChanges();
      return;
    }
    // A new chat minted its conversation — bind to it immediately so the composer and
    // transcript stay responsive (the shell also sets activeId via sessionCreated$).
    if (id !== this.activeId) {
      this.activeId = id;
      this.session = this.store.getSession(id) ?? null;
    }
    this.attachmentScopeId = id;
    this.detectChanges();
  }

  /**
   * True for the last assistant answer once its turn is done — where regenerate shows.
   * A turn whose prompt carried images is excluded: those attachments are bound to the
   * message that sent them and cannot be re-sent, so a re-run would silently answer the
   * text alone.
   */
  canRegenerate(index: number): boolean {
    if (this.streaming || index !== this.messages.length - 1) return false;
    const prompt = this.messages[index - 1];
    return !prompt?.attachments?.length;
  }

  /** Re-run the prompt that produced this answer (ChatGPT/Gemini style), replacing it. */
  regenerate(m: ChatMessage): void {
    if (this.streaming || !this.activeId) return;
    if (m.role !== 'assistant') return;
    this.store.regenerate(this.activeId, this.selectedModel, this.selectedThinkingLevel);
  }

  stop(): void {
    if (this.activeId) this.store.stop(this.activeId);
  }

  /************************************
   * Composer end
   ************************************/

  /************************************
   * Image attachments start
   ************************************/
  /** The attachments that finished uploading and may be sent with the next turn. */
  get readyAttachments(): PendingAttachment[] {
    return this.attachments.filter((item) => item.state === 'ready');
  }

  /** True while any picked image is still uploading — the send button waits for it. */
  get uploadInFlight(): boolean {
    return this.attachments.some((item) => item.state === 'uploading');
  }

  /** True when the composer has nothing to send (no prompt and no ready image). */
  get sendDisabled(): boolean {
    return (
      (!this.input.trim() && this.readyAttachments.length === 0) ||
      this.streaming ||
      this.uploadInFlight
    );
  }

  /** Files chosen via the composer's attach button. The input is reset so picking the
   *  same file again still fires `change`. */
  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    void this.addFiles(Array.from(input.files ?? []));
    input.value = '';
  }

  /** Screenshots and copied images pasted straight into the composer. */
  onComposerPaste(event: ClipboardEvent): void {
    const files = Array.from(event.clipboardData?.files ?? []);
    if (files.length === 0) return;
    event.preventDefault();
    void this.addFiles(files);
  }

  onComposerDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = true;
  }

  onComposerDragLeave(): void {
    this.dragOver = false;
  }

  onComposerDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;
    void this.addFiles(Array.from(event.dataTransfer?.files ?? []));
  }

  /**
   * Validate a batch of picked files, add the survivors to the strip as thumbnails, and
   * start uploading them. Rejections are reported without dropping the whole batch.
   */
  private async addFiles(files: File[]): Promise<void> {
    this.attachError = null;
    if (files.length === 0) return;

    const images = files.filter((file) => isAllowedAttachment(file));
    if (images.length !== files.length) {
      this.attachError = 'Only PNG, JPEG, WebP, and GIF images can be attached.';
    }
    const withinSize = images.filter((file) => file.size <= ATTACHMENT_MAX_BYTES);
    if (withinSize.length !== images.length) {
      this.attachError = `Each image must be under ${formatBytes(ATTACHMENT_MAX_BYTES)}.`;
    }
    const room = ATTACHMENT_MAX_COUNT - this.attachments.length;
    const accepted = withinSize.slice(0, Math.max(0, room));
    if (accepted.length === 0) {
      if (room <= 0) {
        this.attachError = `You can attach up to ${ATTACHMENT_MAX_COUNT} images.`;
      }
      this.detectChanges();
      return;
    }
    if (accepted.length < withinSize.length) {
      this.attachError = `You can attach up to ${ATTACHMENT_MAX_COUNT} images.`;
    }

    const pending: PendingAttachment[] = accepted.map((file) => ({
      localId: uuidV4(),
      file,
      previewUrl: URL.createObjectURL(file),
      progress: 0,
      state: 'uploading',
    }));
    this.attachments = [...this.attachments, ...pending];
    this.detectChanges();
    await this.uploadBatch(pending);
  }

  /**
   * Authorise, upload, and confirm one batch. Authorisation is a single call for the
   * batch; the uploads then run concurrently straight to S3, and confirm is the arbiter
   * of whether each image is usable.
   */
  private async uploadBatch(batch: PendingAttachment[]): Promise<void> {
    const conversationId = this.conversationIdForAttachments();
    let tickets: AttachmentUploadTicket[];
    try {
      const intents = await Promise.all(
        batch.map(async (item) => {
          const { width, height } = await this.api.readImageDimensions(item.file);
          return {
            fileName: item.file.name,
            contentType: item.file.type,
            sizeBytes: item.file.size,
            ...(width ? { width } : {}),
            ...(height ? { height } : {}),
          };
        }),
      );
      tickets = await firstValueFrom(
        this.api.requestAttachmentUploads(conversationId, intents),
      );
      // One ticket per image, in request order — anything else and the pairing below
      // would attribute an upload to the wrong file.
      if (tickets.length !== batch.length) {
        throw new Error('Upload could not be authorised. Please try again.');
      }
    } catch (err) {
      const message = this.attachmentError(err);
      this.attachError = message;
      this.markBatchFailed(batch, message);
      return;
    }

    await Promise.all(
      batch.map(async (item, index) => {
        const ticket = tickets[index];
        // Record the id before uploading, so confirm can still vouch for an image whose
        // upload only *appeared* to fail — a response we could not read, say, or a
        // connection dropped after S3 had already stored the bytes. Confirm inspects the
        // object for real, so it is the safer arbiter than the XHR's outcome.
        this.patchAttachment(item.localId, { attachmentUuid: ticket.attachmentUuid });
        try {
          await this.api.uploadAttachmentToS3(ticket, item.file, (fraction) =>
            this.reportProgress(item.localId, fraction),
          );
          this.patchAttachment(item.localId, { progress: 1 });
        } catch (err) {
          this.patchAttachment(item.localId, {
            state: 'failed',
            error: this.attachmentError(err),
          });
        }
      }),
    );

    try {
      const confirmed = await firstValueFrom(
        this.api.confirmAttachmentUploads(tickets.map((t) => t.attachmentUuid)),
      );
      const byUuid = new Map(confirmed.map((entry) => [entry.attachmentUuid, entry]));
      batch.forEach((item, index) => {
        const outcome = byUuid.get(tickets[index].attachmentUuid);
        if (outcome?.status === 'ready') {
          this.patchAttachment(item.localId, { state: 'ready', progress: 1, error: undefined });
        } else {
          this.patchAttachment(item.localId, {
            state: 'failed',
            error: outcome?.error ?? 'That image could not be used.',
          });
        }
      });
    } catch (err) {
      this.markBatchFailed(batch, this.attachmentError(err));
    }
  }

  /** Drop an image from the strip, releasing its preview and its server-side record. */
  removeAttachment(localId: string): void {
    const target = this.attachments.find((item) => item.localId === localId);
    this.attachments = this.attachments.filter((item) => item.localId !== localId);
    this.attachError = null;
    if (target) {
      URL.revokeObjectURL(target.previewUrl);
      // Best-effort server cleanup; the backend's orphan sweep covers a failure here.
      if (target.attachmentUuid) {
        this.api.deleteAttachment(target.attachmentUuid).subscribe({ error: () => undefined });
      }
    }
    this.detectChanges();
  }

  /**
   * Re-run the upload for one failed image. The batch is re-authorised from scratch, so the
   * retry always carries a fresh presigned POST rather than the ticket that may have expired
   * — and the record the failed attempt reserved is released first, so a retry does not leave
   * a second authorised-but-never-confirmed attachment behind for the orphan sweep.
   */
  retryAttachment(localId: string): void {
    const target = this.attachments.find((item) => item.localId === localId);
    if (!target) return;
    if (target.attachmentUuid) {
      this.api.deleteAttachment(target.attachmentUuid).subscribe({ error: () => undefined });
    }
    this.patchAttachment(localId, {
      state: 'uploading',
      progress: 0,
      error: undefined,
      attachmentUuid: undefined,
    });
    void this.uploadBatch([{ ...target, state: 'uploading', progress: 0, attachmentUuid: undefined }]);
  }

  /**
   * The conversation attachments are authorised against. A new chat mints one client-side
   * rather than calling the init endpoint, so abandoning a draft leaves no empty
   * conversation behind — the chat endpoint creates the row when the first turn runs.
   */
  private conversationIdForAttachments(): string {
    if (this.activeId) {
      this.attachmentScopeId = this.activeId;
      return this.activeId;
    }
    if (!this.attachmentScopeId) this.attachmentScopeId = uuidV4();
    return this.attachmentScopeId;
  }

  private patchAttachment(localId: string, patch: Partial<PendingAttachment>): void {
    this.attachments = this.attachments.map((item) =>
      item.localId === localId ? { ...item, ...patch } : item,
    );
    this.detectChanges();
  }

  /** Progress repaints are throttled to whole percent so a large upload doesn't run
   *  change detection on every chunk. */
  private reportProgress(localId: string, fraction: number): void {
    const item = this.attachments.find((a) => a.localId === localId);
    if (!item) return;
    if (Math.round(item.progress * 100) === Math.round(fraction * 100)) return;
    this.patchAttachment(localId, { progress: fraction });
  }

  private markBatchFailed(batch: PendingAttachment[], error: string): void {
    for (const item of batch) {
      this.patchAttachment(item.localId, { state: 'failed', error });
    }
  }

  private attachmentError(err: unknown): string {
    if (err instanceof Error) return err.message;
    const message = (err as any)?.error?.message ?? (err as any)?.message;
    return typeof message === 'string' && message ? message : 'Upload failed. Please try again.';
  }

  /** Clear the composer's attachments and release every object URL it still owns. */
  private discardAttachments(): void {
    for (const item of this.attachments) URL.revokeObjectURL(item.previewUrl);
    this.attachments = [];
    this.attachError = null;
    this.attachmentScopeId = null;
    this.releasePreviewUrls();
    this.refreshedImages.clear();
    this.brokenImages.clear();
    this.lightbox = null;
  }

  private releasePreviewUrls(): void {
    for (const url of this.sentPreviewUrls) URL.revokeObjectURL(url);
    this.sentPreviewUrls = [];
  }

  formatFileSize(bytes: number): string {
    return formatBytes(bytes);
  }

  /** Round a fraction to a whole percent for the thumbnail's progress bar. */
  progressPercent(fraction: number): number {
    return Math.round(fraction * 100);
  }

  /** Layout modifier for a sent message's thumbnails (1, 2, or 3+ images). */
  imageGridClass(count: number): string {
    return `msg-images msg-images--${Math.min(count, 3)}`;
  }

  /** CSS aspect-ratio for a sent image, so history renders without layout shift. */
  aspectRatio(image: MessageAttachment): string | null {
    return image.width && image.height ? `${image.width} / ${image.height}` : null;
  }

  /**
   * Only a lone thumbnail follows the image's own proportions — two or more are square
   * tiles, so their ratio comes from the stylesheet instead.
   */
  thumbAspectRatio(count: number, image: MessageAttachment): string | null {
    return count === 1 ? this.aspectRatio(image) : null;
  }

  isImageBroken(image: MessageAttachment): boolean {
    return this.brokenImages.has(image.attachmentUuid);
  }

  /**
   * A sent image failed to load. Its presigned URL expires, so re-resolve it once — a
   * tab left open past the view window recovers rather than showing a broken thumbnail.
   */
  onImageError(image: MessageAttachment): void {
    const uuid = image.attachmentUuid;
    // A local object URL can't be re-resolved, and neither can a second failure.
    if (this.refreshedImages.has(uuid) || image.url.startsWith('blob:')) {
      this.brokenImages.add(uuid);
      this.detectChanges();
      return;
    }
    this.refreshedImages.add(uuid);
    this.api.resolveAttachmentUrl(uuid).subscribe({
      next: (url) => {
        if (url) image.url = url;
        else this.brokenImages.add(uuid);
        this.detectChanges();
      },
      error: () => {
        this.brokenImages.add(uuid);
        this.detectChanges();
      },
    });
  }

  openLightbox(image: MessageAttachment): void {
    this.lightbox = image;
  }

  closeLightbox(): void {
    this.lightbox = null;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.lightbox) {
      this.lightbox = null;
      this.detectChanges();
    }
  }

  trackByAttachment(_index: number, item: PendingAttachment): string {
    return item.localId;
  }

  trackByImage(_index: number, image: MessageAttachment): string {
    return image.attachmentUuid;
  }
  /************************************
   * Image attachments end
   ************************************/

  /************************************
   * Feedback (thumbs up / down) start
   ************************************/
  vote(msg: ChatMessage, choice: FeedbackRating): void {
    if (!this.activeId) return;
    // Casting a fresh thumbs-down opens the popup to collect a category + note.
    // Thumbs-up, and re-clicking an active thumb to clear it, stay immediate.
    if (choice === 'down' && msg.feedback !== 'down') {
      this.feedbackTarget = msg;
      return;
    }
    this.store.vote(this.activeId, msg, choice);
  }

  /** Popup submit: record the thumbs-down with its category + note, then close. The
   *  note is bundled with the conversation id and the rated Q&A so the review section
   *  can show that context (the feedback API stores only a free-text message). */
  onFeedbackSubmit(detail: FeedbackDetail): void {
    if (this.activeId && this.feedbackTarget) {
      const message = encodeFeedbackMessage(detail.text, {
        conversationId: this.activeId,
        question: this.questionFor(this.feedbackTarget),
        answer: this.feedbackTarget.text || '',
      });
      this.store.submitFeedback(this.activeId, this.feedbackTarget, detail.category, message);
    }
    this.feedbackTarget = null;
  }

  /** The user turn that prompted an assistant answer — the nearest preceding user message. */
  private questionFor(msg: ChatMessage): string {
    const idx = this.messages.indexOf(msg);
    for (let i = idx - 1; i >= 0; i--) {
      if (this.messages[i].role === 'user') return this.messages[i].text || '';
    }
    return '';
  }

  /** Popup dismissed without submitting — leave the message unrated. */
  onFeedbackCancel(): void {
    this.feedbackTarget = null;
  }
  /************************************
   * Feedback (thumbs up / down) end
   ************************************/

  /************************************
   * Rendering helpers start
   ************************************/
  /** Cut an assistant answer into renderable segments: prose, email drafts, and product
   *  entries — each paragraph or list item that opens on a bold, linked product name. Each
   *  entry takes the `product_cards` card whose `product_link` equals its link (matched on
   *  the link, never the position — same rule as the storefront widget). Cards no entry took
   *  (a product linked only inside a table, say) go in a strip under the settled reply.
   *  A reply with no `product_cards` frame still gets its pictures: each entry whose name
   *  links to a GatherCo storefront product page is looked up by that link instead — this is
   *  also what a streaming reply shows, as the frame lands after the last answer chunk.
   *  Entries are counted across the prose segments in reading order; an email draft is
   *  copy for the customer, never an entry.
   *
   *  Memoised per message: the template calls this every change-detection cycle
   *  (including on every scroll), and returning fresh HTML values each time made
   *  Angular re-set `[innerHTML]`, which rebuilt the inner DOM — so a horizontally
   *  scrolled table snapped back to the left whenever the log was scrolled. Caching by
   *  message identity returns the SAME references while the text and components are
   *  unchanged (it recomputes only as the text grows or a frame lands during
   *  streaming), so the rendered table DOM — and its scroll position — is preserved.
   *  `components` is replaced (never mutated) on each frame, so identity is enough. */
  private partsCache = new WeakMap<
    ChatMessage,
    { text: string; components: PebblComponent[]; parts: MessageParts }
  >();
  messageParts(msg: ChatMessage): MessageParts {
    const text = msg.text || '';
    const cached = this.partsCache.get(msg);
    if (cached && cached.text === text && cached.components === msg.components) return cached.parts;
    const products = cardedProducts(msg.components);
    const prev = cached?.parts;
    // Keep the SKU array references stable so the entries and strip do not re-run ngOnChanges.
    const stable = (old: string[] | undefined, fresh: string[]): string[] =>
      old && old.length === fresh.length && old.every((s, k) => s === fresh[k]) ? old : fresh;
    const skus = stable(prev?.skus, products.map((p) => p.sku));
    const links = products.map((p) => p.link);
    const byLink = !msg.error && !skus.length;

    // Lay out every prose segment first, then pair the entries with their cards in one pass,
    // so a card is used once across the whole reply.
    const runs: { kind: MessageSegmentKind; run: ReplyEntrySegment }[] = [];
    let placed = 0;
    for (const segment of splitMessageSegments(text)) {
      // An errored turn shows its text without pictures.
      if (segment.kind === 'email' || msg.error) {
        runs.push({ kind: segment.kind, run: { html: segment.html, entry: null, followsEntry: false } });
        continue;
      }
      const layout = byLink
        ? placeProductCards(segment.html, Number.MAX_SAFE_INTEGER, placed, { storefrontOnly: true })
        : placeProductCards(segment.html, Number.MAX_SAFE_INTEGER, placed, { links });
      for (const run of layout.segments) runs.push({ kind: segment.kind, run });
      placed += layout.placed;
    }
    const entryRuns = runs.filter(({ run }) => run.entry !== null);
    const pairing = byLink ? [] : pairEntries(entryRuns.map(({ run }) => run.href ?? null), links);
    const cardOf = new Map(entryRuns.map(({ run }, k) => [run, pairing[k] ?? null]));
    const taken = new Set(pairing.filter((card): card is number => card !== null));
    const stripSkus = stable(
      prev?.stripSkus,
      skus.filter((_, k) => !taken.has(k)),
    );

    const reused = new Map((prev?.segments ?? []).map((seg) => [seg.key, seg]));
    const segments: RenderedSegment[] = [];
    let plainCount = 0;
    for (const { kind, run } of runs) {
      const card = cardOf.get(run) ?? null;
      // The frame's `recommended` flag puts the badge on its entry's name line.
      const html = card !== null && products[card]?.recommended ? markRecommendedEntry(run.html) : run.html;
      // Entries key on their place in the reply, so a streamed entry keeps its component
      // (and its loaded picture) while the text after it grows.
      const key = run.entry !== null ? `e${run.entry}` : `${kind === 'email' ? 'x' : 'm'}${plainCount++}`;
      // Model output is untrusted (it can echo knowledge-base / uploaded-document
      // text), so it is cleaned by Angular's allow-list sanitizer rather than
      // marked trusted. Sanitized once here so the clipboard copy gets the same
      // clean markup the template shows.
      const clean = this.sanitizer.sanitize(SecurityContext.HTML, html) ?? '';
      const old = reused.get(key);
      if (
        old &&
        old.kind === kind &&
        old.rawHtml === clean &&
        old.followsEntry === run.followsEntry &&
        old.card === card
      ) {
        segments.push(old);
        continue;
      }
      segments.push({
        key,
        kind,
        rawHtml: clean,
        html: clean,
        entry: run.entry,
        card,
        followsEntry: run.followsEntry,
        href: run.href,
        name: run.name,
      });
    }

    const parts: MessageParts = { segments, skus, placed, stripSkus };
    this.partsCache.set(msg, { text, components: msg.components, parts });
    return parts;
  }

  /** SKUs no entry took go in a strip under the reply — only once it has finished
   *  streaming, so the cards never flash at the bottom and then jump into an entry.
   *  A reply carrying an email draft gets no strip: the draft is the deliverable, and
   *  cards for products it doesn't name as entries are noise beside it. Cards placed on
   *  an entry still render. */
  showUnplacedCards(m: ChatMessage, parts: MessageParts): boolean {
    if (m.streaming || m.error || !parts.stripSkus.length) return false;
    return !parts.segments.some((seg) => seg.kind === 'email');
  }

  /** Tables and metrics — the components that render below the reply. */
  hasBlockComponents(m: ChatMessage): boolean {
    return m.components.some((c) => c.type !== 'product_cards');
  }

  /** Copy an email draft to the clipboard as rich HTML (so Gmail keeps the
   *  formatting) plus a plain-text fallback. */
  copyEmail(key: string, rawHtml: string): void {
    void this.copyRichHtml(rawHtml).then((ok) => {
      if (!ok) return;
      this.copiedEmail = key;
      // The clipboard promise resolves in a microtask, outside this OnPush
      // component's change detection — refresh now so "Copied" shows on the
      // first click, not only after some later tick.
      this.detectChanges();
      setTimeout(() => {
        this.copiedEmail = null;
        this.detectChanges();
      }, 1400);
    });
  }

  asTable(component: PebblComponent): TableComponent | null {
    return component.type === 'table' ? component : null;
  }

  hasContent(m: ChatMessage): boolean {
    return !!(m.text || m.components.length > 0 || m.error);
  }

  /** Compact cost label shown next to the copy icon, e.g. "$0.0042". */
  formatCost(amount: number, currency: string): string {
    const symbol = currency === 'USD' ? '$' : `${currency} `;
    return `${symbol}${amount.toFixed(4)}`;
  }

  /** Cost + token-usage tooltip for the cost icon, e.g. "Query cost: $0.0042 · 1,284 tokens". */
  usageTip(usage: UsageInfo): string {
    const tokens = (usage.totalTokens ?? 0).toLocaleString('en-US');
    return `Query cost: ${this.formatCost(usage.totalCost, usage.currency)} · ${tokens} tokens`;
  }

  /** Copy the whole answer as it reads on screen - rich HTML for Gmail, Outlook
   *  and Word, plain text without the Markdown marks for everything else. Copying
   *  `text` as-is pasted the raw Markdown: literal `**` and `|` table rows. */
  copyAnswer(index: number, text: string): void {
    // Model output is untrusted, and `copyRichHtml` writes it with `element.innerHTML`,
    // which skips the template sanitizer — so clean it here, as the on-screen segments are.
    const html = this.sanitizer.sanitize(SecurityContext.HTML, renderMarkdownToHtml(text)) ?? '';
    void this.copyRichHtml(html).then((ok) => {
      if (!ok) return;
      this.copiedAnswer = index;
      // Resolves in a microtask, outside this OnPush component's change
      // detection — refresh now so "Copied" appears on the first click.
      this.detectChanges();
      setTimeout(() => {
        this.copiedAnswer = null;
        this.detectChanges();
      }, 1400);
    });
  }

  copyTableCsv(index: number, table: TableComponent): void {
    void this.copyText(this.tableToCsv(table)).then((ok) => {
      if (!ok) return;
      this.copiedTable = index;
      // Resolves in a microtask, outside this OnPush component's change
      // detection — refresh now so "Copied" appears on the first click.
      this.detectChanges();
      setTimeout(() => {
        this.copiedTable = null;
        this.detectChanges();
      }, 1400);
    });
  }

  downloadTableCsv(table: TableComponent): void {
    const blob = new Blob([this.tableToCsv(table)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pebbl-table.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  trackByIndex(index: number): number {
    return index;
  }
  /************************************
   * Rendering helpers end
   ************************************/

  /************************************
   * Internal helpers start
   ************************************/
  private tableToCsv(table: TableComponent): string {
    const cell = (v: unknown): string => {
      const s = v === null || v === undefined ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [table.columns.map(cell).join(',')];
    for (const row of table.rows) lines.push(row.map(cell).join(','));
    return lines.join('\n');
  }

  private async copyText(text: string): Promise<boolean> {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {
        /* fall through */
      }
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }

  /** Copy rich HTML to the clipboard (text/html + text/plain) so pasting into
   *  Gmail keeps the formatting, with an execCommand selection fallback. */
  private async copyRichHtml(bodyHtml: string): Promise<boolean> {
    const html = stripInlineHandlers(this.buildEmailClipboardHtml(bodyHtml));
    const plain = htmlToPlainText(html);
    // The charset meta stops Outlook reading non-ASCII (a "×" in a size, a curly
    // apostrophe) as mojibake.
    const clipboardHtml = `<meta charset="utf-8">${html}`;
    // Primary: a SYNCHRONOUS, in-gesture rich copy (hidden selection + execCommand).
    // This lands the copy on the FIRST click. `navigator.clipboard.write`
    // (the fallback below) frequently rejects on the first interaction; because we'd
    // have to `await` it before falling back, the fallback then ran in a microtask —
    // outside the user gesture — where execCommand is blocked too, so nothing copied
    // until a second click. Running execCommand first, still inside the gesture, fixes
    // that.
    try {
      // Not contenteditable: selecting inside an editable element moves focus to it,
      // which left focus on <body> after the copy (and, focused back by hand, would
      // show the button's focus ring and tooltip after a mouse click).
      const el = document.createElement('div');
      el.style.position = 'fixed';
      el.style.left = '-9999px';
      el.style.userSelect = 'text';
      // Explicit light colors in case a browser serializes the selection itself
      // (the app's dark-theme computed styles paste as a black block in Gmail).
      el.style.color = '#1b1c1d';
      el.style.background = '#ffffff';
      el.innerHTML = html;
      document.body.appendChild(el);
      const range = document.createRange();
      range.selectNodeContents(el);
      const sel = window.getSelection();
      // Whatever the user had selected is put back once the copy is done.
      const saved = sel ? Array.from({ length: sel.rangeCount }, (_, i) => sel.getRangeAt(i)) : [];
      sel?.removeAllRanges();
      sel?.addRange(range);
      // Hand the browser both flavours ourselves instead of letting it serialise the
      // selection: its own text/plain drops the list markers and adds blank lines,
      // and its text/html can carry the app's computed styles.
      const onCopy = (event: ClipboardEvent): void => {
        if (!event.clipboardData) return;
        event.clipboardData.setData('text/html', clipboardHtml);
        event.clipboardData.setData('text/plain', plain);
        event.preventDefault();
      };
      document.addEventListener('copy', onCopy, true);
      let ok = false;
      try {
        ok = document.execCommand('copy');
      } finally {
        document.removeEventListener('copy', onCopy, true);
        sel?.removeAllRanges();
        saved.forEach((r) => sel?.addRange(r));
        document.body.removeChild(el);
      }
      if (ok) return true;
    } catch {
      /* fall through to the async Clipboard API */
    }
    // Fallback: the async Clipboard API (rich text/html + text/plain) for any browser
    // that no longer supports execCommand.
    const win = window as unknown as { ClipboardItem?: typeof ClipboardItem };
    if (navigator.clipboard?.write && typeof win.ClipboardItem === 'function') {
      try {
        await navigator.clipboard.write([
          new win.ClipboardItem({
            'text/html': new Blob([clipboardHtml], { type: 'text/html' }),
            'text/plain': new Blob([plain], { type: 'text/plain' }),
          }),
        ]);
        return true;
      } catch {
        return false;
      }
    }
    return false;
  }

  /** Build theme-neutral HTML for the clipboard: strip the app's dark-theme
   *  color/background (so it doesn't paste as a black block in Gmail) and keep
   *  only structural formatting — bold, bullets, tables, links. Gmail strips
   *  classes and <style>, so the table/link styles are inlined. */
  private buildEmailClipboardHtml(bodyHtml: string): string {
    const tmp = document.createElement('div');
    tmp.innerHTML = bodyHtml;
    // A task list's checkboxes do not survive a paste (Gmail strips <input>, Quill
    // turns each into a stray empty line), so carry the ticked state as text. marked
    // puts a newline between the box and its label; drop it so they share a line.
    tmp.querySelectorAll('input[type="checkbox"]').forEach((box) => {
      const label = box.nextSibling;
      if (label?.nodeType === Node.TEXT_NODE) label.textContent = (label.textContent ?? '').replace(/^\s+/, '');
      box.replaceWith(document.createTextNode((box as HTMLInputElement).checked ? '\u2611 ' : '\u2610 '));
    });
    this.keepLineBreaks(tmp);
    // An internal note's hint sits beside its label on screen by styling alone; with
    // the classes gone the two would run together ("Not in this draftinternal - …").
    tmp.querySelectorAll('.not-in-draft__hint').forEach((hint) => {
      hint.textContent = ` (${(hint.textContent ?? '').trim()})`;
    });
    // Drop classes and any inline color/background so the draft inherits Gmail's
    // white background and default dark text instead of the console's theme.
    tmp.querySelectorAll<HTMLElement>('*').forEach((node) => {
      node.removeAttribute('class');
      const style = node.getAttribute('style');
      if (style) {
        const cleaned = style
          .split(';')
          .map((decl) => decl.trim())
          .filter((decl) => decl && !/^(color|background(-color)?)\s*:/i.test(decl))
          .join(';');
        if (cleaned) node.setAttribute('style', cleaned);
        else node.removeAttribute('style');
      }
    });
    tmp.querySelectorAll('table').forEach((t) =>
      t.setAttribute('style', 'border-collapse:collapse;border:1px solid #d9d9d9;margin:10px 0;font-size:14px;'),
    );
    // Keep a column's own alignment (`---:` marks figures, which marked emits as
    // align="right") - the inline style would otherwise override it to left.
    const align = (c: Element): string => c.getAttribute('align') || 'left';
    tmp.querySelectorAll('th').forEach((c) =>
      c.setAttribute('style', `border:1px solid #d9d9d9;padding:6px 10px;text-align:${align(c)};background:#f4f4f4;`),
    );
    tmp.querySelectorAll('td').forEach((c) =>
      c.setAttribute('style', `border:1px solid #d9d9d9;padding:6px 10px;text-align:${align(c)};`),
    );
    tmp.querySelectorAll('a').forEach((a) => a.setAttribute('style', 'color:#1155cc;'));
    // Neutral wrapper: default dark text, no background — formatting only.
    return `<div style="color:#1b1c1d;font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.5;">${tmp.innerHTML}</div>`;
  }

  /** The chat shows replies with `white-space: pre-wrap`, so a single newline inside a
   *  paragraph ("Kind regards,\nThe GatherCo Team") is a line break on screen. Pasted
   *  HTML is read with normal white-space, where it would collapse to a space, so turn
   *  those newlines into <br>. Newlines that only sit between blocks (the renderer's own
   *  formatting around table rows, list items and paragraphs) are dropped instead: kept,
   *  they put stray empty lines in Quill. Code keeps its text untouched. */
  private keepLineBreaks(root: HTMLElement): void {
    const isBlock = (n: Node | null): boolean =>
      n instanceof HTMLElement &&
      /^(ADDRESS|ARTICLE|ASIDE|BLOCKQUOTE|DD|DIV|DL|DT|FIGURE|FOOTER|H[1-6]|HEADER|HR|LI|OL|P|PRE|SECTION|TABLE|TBODY|TD|TFOOT|TH|THEAD|TR|UL)$/.test(
        n.tagName,
      );
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const texts: Text[] = [];
    while (walker.nextNode()) texts.push(walker.currentNode as Text);
    for (const node of texts) {
      if (!node.data.includes('\n') || node.parentElement?.closest('pre, code')) continue;
      let text = node.data;
      // At either edge of a block a newline is not a visible break.
      if (node.previousSibling ? isBlock(node.previousSibling) : isBlock(node.parentNode)) {
        text = text.replace(/^\s*\n/, '');
      }
      if (node.nextSibling ? isBlock(node.nextSibling) : isBlock(node.parentNode)) {
        text = text.replace(/\n\s*$/, '');
      }
      if (!text) {
        node.remove();
      } else if (!text.includes('\n')) {
        node.data = text;
      } else {
        const lines = document.createDocumentFragment();
        text.split('\n').forEach((line, i) => {
          if (i) lines.appendChild(document.createElement('br'));
          if (line) lines.appendChild(document.createTextNode(line));
        });
        node.replaceWith(lines);
      }
    }
  }

  /** Intercept a manual text selection copy inside an email draft so it lands on
   *  the clipboard theme-neutral (no dark background) instead of the browser's
   *  default serialization of the app's computed dark-theme styles. Works for
   *  partial selections too — it cleans whatever the user actually selected. */
  onEmailCopy(event: ClipboardEvent): void {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      return;
    }
    const fragment = document.createElement('div');
    fragment.appendChild(selection.getRangeAt(0).cloneContents());
    const html = this.buildEmailClipboardHtml(fragment.innerHTML);
    const plain = htmlToPlainText(html);
    if (event.clipboardData) {
      event.clipboardData.setData('text/html', html);
      event.clipboardData.setData('text/plain', plain);
      event.preventDefault();
    }
  }

  /** Read a cookie value by name, or '' if absent. */
  private readCookie(name: string): string {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : '';
  }

  /** Write a cookie that persists across sessions (SameSite=Lax, path=/). */
  private writeCookie(name: string, value: string, maxAgeSeconds: number): void {
    document.cookie =
      `${name}=${encodeURIComponent(value)};max-age=${maxAgeSeconds};path=/;SameSite=Lax`;
  }
  /************************************
   * Internal helpers end
   ************************************/
}
